import React, {
  useEffect, useRef, useState, useCallback,
} from 'react';
import { getVideosDetail } from 'Api/apiFunctions';
import { generateS3Url } from 'Api/awsConfig';
import NetflixPlayer from 'Components/NetflixPlayer';
import styles from './GiftMessagePlayer.module.css';

const formatTime = (sec) => {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// Resolve a clip's playable URL on demand. Two paths:
//  • Existing-user gift flow: clip came from getGiftPendingActions, so it
//    arrives with `isPending: true` and a pre-computed S3 `videoUrl`
//    (or just `fileKey` we can splice onto the S3 base).
//  • New-user / standard flow: clip came from getMyContentsBasedOnUploadDate,
//    so we hit getVideosDetail to fetch `video_raw_file`.
// Resolutions are cached per session via the ref so re-watching is instant.
const resolveClipUrl = async (clip, urlCacheRef) => {
  if (urlCacheRef.current.has(clip.id)) return urlCacheRef.current.get(clip.id);

  // Pending (existing-user) path — URL is already on the clip.
  if (clip.isPending) {
    if (clip.videoUrl) {
      urlCacheRef.current.set(clip.id, clip.videoUrl);
      return clip.videoUrl;
    }
    if (clip.fileKey) {
      const url = `${generateS3Url()}${clip.fileKey}`;
      urlCacheRef.current.set(clip.id, url);
      return url;
    }
    return '';
  }

  // Registered-video path — fetch the playable URL from the API.
  try {
    const response = await getVideosDetail({ id: clip.id });
    const url = response?.data?.data?.video_raw_file || '';
    if (url) urlCacheRef.current.set(clip.id, url);
    return url;
  } catch (e) {
    return '';
  }
};

// ──────────────────────────────────────────────────────────────────────
// Main component: floating trigger + scratch popup + video player modal.
// ──────────────────────────────────────────────────────────────────────
// Now CONTROLLED by the parent: parent decides WHEN to mount us via the
// `open` prop and clears it via `onClose` when we're fully done. Internally
// we manage the wrap → player transition so closing the wrap to play the
// video doesn't unmount the whole component.
const GiftMessagePlayer = ({
  clips, fromName, open, onClose,
}) => {
  const total = clips ? clips.length : 0;
  const senderLabel = (fromName && fromName.trim()) || 'Someone special';

  // Wrap visibility is internal; toggled when the user transitions to the
  // player. Reset whenever the parent re-opens us (open: false → true).
  const [wrapVisible, setWrapVisible] = useState(true);
  const [playerOpen, setPlayerOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setWrapVisible(true);
      setPlayerOpen(false);
    }
  }, [open]);

  const scratchOpen = !!open && wrapVisible && !playerOpen;
  const [activeIndex, setActiveIndex] = useState(0);
  const [intro, setIntro] = useState(false);
  const [muted, setMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentUrl, setCurrentUrl] = useState('');
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [showCompleted, setShowCompleted] = useState(false);

  const videoRef = useRef(null);
  const urlCacheRef = useRef(new Map());

  const activeClip = clips && clips[activeIndex] ? clips[activeIndex] : null;

  // ── Body scroll lock while either modal is open ──
  useEffect(() => {
    if (!scratchOpen && !playerOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [scratchOpen, playerOpen]);

  // ── Resolve current clip URL when player opens / advances ──
  useEffect(() => {
    if (!playerOpen || !activeClip) return undefined;
    let cancelled = false;
    setLoadingUrl(true);
    setCurrentUrl('');
    resolveClipUrl(activeClip, urlCacheRef).then((url) => {
      if (cancelled) return;
      setCurrentUrl(url);
      setLoadingUrl(false);
    });
    return () => { cancelled = true; };
  }, [playerOpen, activeClip]);

  // Reset transient playback state when activeIndex changes.
  useEffect(() => {
    setProgress(0);
    setElapsed(0);
    setDuration(0);
  }, [activeIndex]);

  // ── Duration probe for MediaRecorder WebM clips ──
  // Recorder-produced WebM files report `duration === Infinity` until the
  // media is seeked, so the live element's `loadedmetadata` never yields a
  // usable duration (seekbar max collapses to 0 and the timecode shows 0:00).
  // We resolve the real duration on a DETACHED throwaway element — never the
  // live one — by force-seeking to the end, which makes the browser read the
  // container to completion and emit a finite duration. This keeps the visible
  // player's playback untouched (seeking the live WebM stalls/corrupts it).
  useEffect(() => {
    if (!currentUrl) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    const probe = document.createElement('video');
    probe.preload = 'metadata';
    probe.muted = true;
    probe.crossOrigin = 'anonymous';

    const cleanup = () => {
      controller.abort();
      probe.removeAttribute('src');
      try { probe.load(); } catch (e) { /* noop */ }
    };
    const settle = () => {
      if (cancelled) return false;
      if (Number.isFinite(probe.duration) && probe.duration > 0) {
        setDuration(probe.duration);
        cleanup();
        return true;
      }
      return false;
    };
    const onMeta = () => {
      if (settle()) return;
      // Duration still Infinity/NaN → force the browser to compute it.
      try { probe.currentTime = 1e101; } catch (e) { /* noop */ }
    };

    const opts = { signal: controller.signal };
    probe.addEventListener('loadedmetadata', onMeta, opts);
    probe.addEventListener('durationchange', settle, opts);
    probe.addEventListener('timeupdate', settle, opts);
    probe.src = currentUrl;

    return () => { cancelled = true; cleanup(); };
  }, [currentUrl]);

  // Closing the wrap = closing everything. Notify parent to unmount us.
  const handleCloseScratch = () => {
    setWrapVisible(false);
    if (typeof onClose === 'function') onClose();
  };

  // Hide the wrap and reveal the player. Component stays mounted because
  // parent's `open` is still true.
  const handleStartPlayback = () => {
    setActiveIndex(0);
    setShowCompleted(false);
    setMuted(false);
    setIntro(true);
    setWrapVisible(false);
    setPlayerOpen(true);
    setTimeout(() => setIntro(false), 1500);
  };

  // Closing the player = closing everything. Notify parent.
  const handleClosePlayer = useCallback(() => {
    if (videoRef.current) {
      try { videoRef.current.pause(); } catch (e) { /* noop */ }
    }
    setPlayerOpen(false);
    setIsPlaying(false);
    if (typeof onClose === 'function') onClose();
  }, [onClose]);

  // ── Video event handlers ──
  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);
  const handleEnded = () => {
    setIsPlaying(false);
    // Backfill duration from the final playhead for WebM clips that never
    // reported a finite duration during playback.
    const v = videoRef.current;
    if (v && v.currentTime > 0) setDuration((d) => (d > 0 ? d : v.currentTime));
    if (activeIndex < total - 1) {
      setActiveIndex((i) => i + 1);
    } else {
      setShowCompleted(true);
    }
  };
  // The live element only yields a finite duration for well-formed media; for
  // Infinity-duration WebM the detached probe effect fills `duration` instead.
  const handleLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v) return;
    if (Number.isFinite(v.duration) && v.duration > 0) setDuration(v.duration);
  };
  const handleDurationChange = () => {
    const v = videoRef.current;
    if (v && Number.isFinite(v.duration) && v.duration > 0) setDuration(v.duration);
  };
  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    setElapsed(v.currentTime);
    // Prefer the element's own duration; fall back to the probed `duration`
    // state when the element still reports Infinity/0.
    const dur = Number.isFinite(v.duration) && v.duration > 0 ? v.duration : duration;
    if (dur > 0) setProgress(Math.min(v.currentTime / dur, 1));
  };

  // Auto-play the active clip once URL + intro settle. Modal entry was a
  // user gesture, so unmuted autoplay is allowed; we still fall back to muted
  // if a particular browser blocks it.
  useEffect(() => {
    if (!playerOpen || intro || !currentUrl || !videoRef.current) return;
    const v = videoRef.current;
    v.muted = muted;
    const p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        setMuted(true);
        v.muted = true;
        v.play().catch(() => { /* noop */ });
      });
    }
  }, [playerOpen, intro, currentUrl, activeIndex, muted]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  // ── Player actions ──
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused || v.ended) {
      v.play().catch(() => { /* noop */ });
    } else {
      v.pause();
    }
  };
  const toggleMute = () => setMuted((m) => !m);
  const goPrev = () => {
    setShowCompleted(false);
    setActiveIndex((i) => Math.max(0, i - 1));
  };
  const goNext = () => {
    if (activeIndex < total - 1) {
      setActiveIndex((i) => i + 1);
    } else {
      setShowCompleted(true);
      const v = videoRef.current;
      if (v) try { v.pause(); } catch (e) { /* noop */ }
    }
  };
  const jumpTo = (i) => {
    setShowCompleted(false);
    setActiveIndex(i);
  };
  const handleScrub = (e) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    const target = Number(e.target.value);
    v.currentTime = target;
    setElapsed(target);
    setProgress(target / duration);
  };
  const replay = () => {
    setShowCompleted(false);
    setActiveIndex(0);
  };

  // Esc / arrows in player
  useEffect(() => {
    if (!playerOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') handleClosePlayer();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === ' ' || e.key === 'k') {
        e.preventDefault();
        togglePlay();
      } else if (e.key === 'm') toggleMute();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [playerOpen, activeIndex, total, handleClosePlayer]);

  if (!clips || clips.length === 0) return null;

  return (
    <>
      {/* ── Gift wrap popup (controlled by parent via `open` prop) ── */}
      {scratchOpen && (
        <div className={styles.scratchModalRoot} role="dialog" aria-modal="true" aria-label="Open your gift">
          <div className={styles.scratchBackdrop} aria-hidden="true">
            {Array.from({ length: 18 }, (_, i) => (
              <span
                key={i}
                className={styles.scratchSparkle}
                style={{
                  '--sx': `${(i * 53) % 100}%`,
                  '--sy': `${(i * 79) % 100}%`,
                  '--sd': `${1.4 + (i % 4) * 0.4}s`,
                  '--sdelay': `${(i % 6) * 0.18}s`,
                }}
              />
            ))}
          </div>

          <div className={styles.scratchCard}>
            <button
              type="button"
              className={styles.scratchClose}
              onClick={handleCloseScratch}
              aria-label="Close"
            >
              ✕
            </button>

            {/* Revealed gift content — same styling as before, just shown directly. */}
            <div className={styles.scratchSurface}>
              <div className={styles.revealLayer}>
                <div className={styles.revealEyebrow}>YOU&apos;VE GOT A GIFT</div>
                <div className={styles.revealEmoji} aria-hidden="true">🎁</div>
                <div className={styles.revealName}>{senderLabel}</div>
                <div className={styles.revealMeta}>
                  {`recorded ${total} ${total === 1 ? 'message' : 'messages'} for you`}
                </div>
              </div>
            </div>

            <button
              type="button"
              className={`${styles.scratchPlayBtn} ${styles.scratchPlayBtnReady}`}
              onClick={handleStartPlayback}
            >
              <span className={styles.scratchPlayIcon} aria-hidden="true" />
              Play Your Gift
            </button>
          </div>
        </div>
      )}

      {/* ── Video player modal (auto-advancing) ── */}
      {playerOpen && (
        <div className={styles.modalRoot} role="dialog" aria-modal="true" aria-label="Gift message player">
          <div className={styles.backdrop} aria-hidden="true">
            {Array.from({ length: 22 }, (_, i) => (
              <span
                key={i}
                className={styles.sparkle}
                style={{
                  '--sx': `${(i * 47) % 100}%`,
                  '--sy': `${(i * 73) % 100}%`,
                  '--sd': `${1 + (i % 5) * 0.4}s`,
                  '--sdelay': `${(i % 7) * 0.18}s`,
                }}
              />
            ))}
          </div>

          {intro && (
            <button type="button" className={styles.intro} onClick={() => setIntro(false)} aria-label="Skip intro">
              <span className={styles.introBox}>🎁</span>
              <span className={styles.introBurst} aria-hidden="true">
                {Array.from({ length: 12 }, (_, i) => (
                  <span key={i} className={styles.introBurstPiece} style={{ '--ang': `${(i * 360) / 12}deg` }} />
                ))}
              </span>
              <span className={styles.introHint}>{`Opening gift from ${senderLabel}…`}</span>
            </button>
          )}

          {!intro && (
            <div className={styles.player}>
              <div className={styles.topBar}>
                <div className={styles.chapterStrip}>
                  {clips.map((clip, i) => (
                    <button
                      key={clip.id}
                      type="button"
                      onClick={() => jumpTo(i)}
                      className={[
                        styles.chapter,
                        i === activeIndex && styles.chapterActive,
                        i < activeIndex && styles.chapterDone,
                      ].filter(Boolean).join(' ')}
                      aria-label={`Play ${i + 1}: ${clip.subject}`}
                      title={clip.subject}
                    >
                      <span className={styles.chapterIndex}>{i + 1}</span>
                      <span
                        className={styles.chapterFill}
                        style={i === activeIndex ? { transform: `scaleX(${progress})` } : undefined}
                      />
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className={styles.closeBtn}
                  onClick={handleClosePlayer}
                  aria-label="Close gift player"
                >
                  ✕
                </button>
              </div>

              <div className={styles.caption}>
                <span className={styles.captionEyebrow}>{`${activeIndex + 1} of ${total}`}</span>
                <h2 className={styles.captionTitle}>{activeClip?.subject || ''}</h2>
              </div>

              <div className={styles.stage}>
                {loadingUrl && (
                  <div className={styles.stageOverlay}>
                    <span className={styles.spinner} />
                    <span>Loading…</span>
                  </div>
                )}
                {!loadingUrl && !currentUrl && (
                  <div className={styles.stageOverlay}>
                    <span>Could not load this clip.</span>
                  </div>
                )}
                {currentUrl && (
                  <NetflixPlayer
                    key={activeClip?.id}
                    ref={videoRef}
                    src={currentUrl}
                    poster={activeClip?.thumbnail}
                    fillHeight
                    onPlay={handlePlay}
                    onPause={handlePause}
                    onEnded={handleEnded}
                    onLoadedMetadata={handleLoadedMetadata}
                    onDurationChange={handleDurationChange}
                    onTimeUpdate={handleTimeUpdate}
                  />
                )}
                {showCompleted && (
                  <div className={styles.completed}>
                    <span className={styles.completedEmoji}>🎉</span>
                    <h3>{`That's the whole gift from ${senderLabel}`}</h3>
                    <div className={styles.completedActions}>
                      <button type="button" className={styles.secondaryBtn} onClick={replay}>Replay</button>
                      <button type="button" className={styles.primaryBtn} onClick={handleClosePlayer}>Close</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Playlist nav row — NetflixPlayer owns play/pause/scrub/mute
                  inside the stage, so this strip only needs prev / next
                  clip controls + the X-of-N indicator. */}
              <div className={styles.controls}>
                <button
                  type="button"
                  className={styles.iconBtn}
                  onClick={goPrev}
                  disabled={activeIndex === 0}
                  aria-label="Previous clip"
                >
                  ⏮
                </button>
                <span className={styles.timeText}>
                  {`Clip ${activeIndex + 1} of ${total}`}
                </span>
                <button
                  type="button"
                  className={styles.iconBtn}
                  onClick={goNext}
                  disabled={activeIndex === total - 1 && showCompleted}
                  aria-label="Next clip"
                >
                  ⏭
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default GiftMessagePlayer;
